import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import { FC, useCallback, useState } from 'react';
import {
  getAuthorizationHeader,
  useAddMultipleEmployeesMutation,
  useGetDateUploadStoplistQuery,
} from 'redux/Api/employedsApi/employedsApi';
import { getFileNameFromContentDisposition } from 'utils/strActions';
import styles from './EmployeesUploadStopListModal.module.scss';
import { ActionButtons } from './components/ActionButtons';
import { FileInfoDisplay } from './components/FileInfoDisplay';
import { FileUploadArea } from './components/FileUploadArea';
import { ReportDownload } from './components/ReportDownload';
import { StatusInfo } from './components/StatusInfo';
import { TemplateLink } from './components/TemplateLink';
import ContentLoader from 'react-content-loader';

interface UploadResponse {
  import_count: number;
  upload_count: number;
  report_file: string;
}

interface EmployeesUploadStopListModalTypes {
  setShow: (e: boolean) => void;
  show: boolean;
}

export const EmployeesUploadStopListModal: FC<EmployeesUploadStopListModalTypes> = ({
  setShow,
  show,
}) => {
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const [reportError, setReportError] = useState('');
  const [addMultipleEmployees, { isLoading }] = useAddMultipleEmployeesMutation();
  const { data: dateUploadStoplist, isLoading: isLoadingDateUploadStoplist } =
    useGetDateUploadStoplistQuery();

  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<'select' | 'completed'>('select');

  const uploadHandler = async () => {
    if (file) {
      const formData = new FormData();
      formData.append('worker_stoplist', file);
      formData.append('persist', '1');

      await addMultipleEmployees(formData)
        .then((data: any) => {
          if (data?.data) {
            setUploadResponse(data.data);
            setStep('completed');
          } else if (data?.error) {
            setUploadResponse(null);
            setFile(null);
            setReportError(
              data?.error?.data?.errors?.worker_stoplist?.[0] ??
                'Ошибка при загрузке файла.',
            );
          }
        })
        .catch(() => {
          setReportError('Ошибка при загрузке файла.');
        });
    }
  };

  const handleDownloadReport = async () => {
    if (uploadResponse?.report_file) {
      const response = await fetch(uploadResponse.report_file, {
        method: 'GET',
        headers: { Authorization: getAuthorizationHeader() },
      });

      const type = response.headers.get('content-type') || undefined;
      const disposition = response.headers.get('content-disposition');
      const arrayBuffer = await response.blob();

      const blob = new Blob([arrayBuffer], {
        type,
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getFileNameFromContentDisposition(disposition));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setReportError('');
    setStep('select');
    setUploadResponse(null);
  }, []);

  const truncateFileName = (name: string, maxLength = 20) => {
    if (name.length <= maxLength) return name;
    const extension = name.split('.').pop();
    const baseName = name.substring(0, name.lastIndexOf('.'));
    return `${baseName.substring(0, maxLength - extension!.length - 3)}...${extension}`;
  };

  const resetForm = () => {
    setFile(null);
    setUploadResponse(null);
    setStep('select');
    setReportError('');
  };

  const renderCompletedStep = () => (
    <div className={styles.previewInfo}>
      <StatusInfo
        importCount={uploadResponse!.import_count}
        uploadCount={uploadResponse!.upload_count}
        isPreview={false}
      />
      <ReportDownload onDownload={handleDownloadReport} />
    </div>
  );

  const renderSelectStep = () => (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <span className={styles.subTitle}>
          Скачай, заполни и загрузи реестр бывших сотрудников. Приглашение их в качестве
          исполнителей недоступно, т.к. имели место трудовые отношения менее 24 мес. назад
        </span>
        <TemplateLink
          hasFile={!!file}
          dateUploadReestr={dateUploadStoplist?.last_update_date}
        />
      </div>
      {file ? (
        <>
          <FileInfoDisplay
            file={file}
            onRemove={() => setFile(null)}
            truncateFileName={truncateFileName}
          />
          <ActionButtons
            onBack={resetForm}
            onPrimaryAction={uploadHandler}
            isLoading={isLoading}
            primaryButtonText={isLoading ? 'Загружаем' : 'Загрузить'}
            primaryActionIcon='done'
          />
        </>
      ) : (
        <FileUploadArea onFileSelect={handleFileSelect} error={reportError} />
      )}
    </div>
  );

  return (
    <Modal setOpen={setShow} open={show}>
      {isLoadingDateUploadStoplist ? (
        <div className={styles.root}>
          <ContentLoader
            speed={2}
            height={400}
            width='100%'
            className={styles.tab_loader}
            backgroundColor='#FFFFFF'
            foregroundColor='#D8E4FB8C'>
            <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
          </ContentLoader>
        </div>
      ) : (
        <div className={styles.root}>
          <div>
            <Title title='Бывшие сотрудники' />
          </div>

          {step === 'completed' && uploadResponse && renderCompletedStep()}
          {step === 'select' && renderSelectStep()}
        </div>
      )}
    </Modal>
  );
};
